import React from 'react';
import { CircularProgress } from '@mui/material';
import { Text } from '@fragments';
import { IconAlertCircle, IconCheck, IconCircle, IconClock, IconMovie } from '@tabler/icons-react';
import StudioAi from '../../../../components/StudioAi';
import { formatDuration } from '../../format';
import './styles.scss';

// `comparing` (2.5.0) runs only when the quote sampled reels by reach; a mapping that skips
// it shows the step as passed once it analyzes.
const STAGES = ['downloading', 'transcribing', 'comparing', 'analyzing'];
const RUNNING = ['queued', 'running'];
const SOURCE_STATUS = { pending: 'personalitySourcePending', downloading: 'personalitySourceDownloading', transcribing: 'personalitySourceTranscribing', comparing: 'personalitySourceComparing',
  analyzing: 'personalitySourceAnalyzing', failed: 'personalitySourceFailed' };
// While the mapping runs, a Reel already transcribed moves on with it.
const READY_WHILE = { transcribing: 'transcribed', comparing: 'comparing', analyzing: 'analyzing' };

// What a chosen Reel is going through. The contract stops at `ready` once its transcript is
// in, but the mapping goes on: the other Reels and, since 2.5.0, the reels of the comparison
// by reach are transcribed, then compared, then everything is analyzed. So a ready Reel keeps
// working with the stage (its word count while transcribing), and one downloaded that waits
// its transcription, which the contract still reads `downloading`, shows as transcribing.
const sourceState = (source, mapping) => {
  if (!RUNNING.includes(mapping.status)) return source.status;
  if (source.status === 'ready') return READY_WHILE[mapping.stage] || 'ready';
  if (source.status === 'downloading' && mapping.stage === 'transcribing') return 'transcribing';
  return source.status;
};
const WORKING = ['downloading', 'transcribing', 'transcribed', 'comparing', 'analyzing'];
const COUNTED = ['ready', 'transcribed'];

// A Reel's state as a chip: a spinner and a shimmering label while it is worked on, a clock
// while it waits its turn, a check once ready (with its word count), the error tone if it
// could not be used.
function SourceChip({ t, source, state }) {
  const working = WORKING.includes(state);
  const left = working ? <CircularProgress className="studio-mapping-progress__spinner" size={12} thickness={5} color="inherit" aria-hidden="true" />
    : state === 'ready' ? <IconCheck /> : state === 'failed' ? <IconAlertCircle /> : <IconClock />;
  const label = COUNTED.includes(state) ? (source.words != null ? t('personalitySourceWords', { count: source.words }) : t('personalitySourceReady'))
    : state === 'failed' ? t(`personalitySourceFailed_${source.error_code}`, { defaultValue: t(SOURCE_STATUS.failed) }) : t(SOURCE_STATUS[state]);
  return <span className={`studio-mapping-progress__chip is-${state}`}>
    <Text.caption contrast={state === 'failed' ? 9 : working ? 8 : 7} weight={500} loading={working} left={left}>{label}</Text.caption>
  </span>;
}

// A mapping on its way: the AI orb with what it is doing, the four stages (download,
// transcription, comparison by reach, analysis) and each chosen Reel with its own state. A Reel transcribed by an
// earlier mapping is ready from the start. Closing the dialog does not stop it; until it
// completes the previous personality, if any, stays in use.
export default function MappingProgress({ studio: s, mapping, hasPersonality }) {
  const { t } = s;
  const current = STAGES.indexOf(mapping.stage);
  return <div className="studio-mapping-progress">
    <div className="studio-mapping-progress__head">
      <StudioAi size={44} active />
      <div className="studio-mapping-progress__copy">
        <Text.h5 weight={500} contrast={9}>{t(mapping.status === 'queued' ? 'personalityQueued' : `personalityStage_${mapping.stage}`)}</Text.h5>
        <Text.p contrast={6}>{t(hasPersonality ? 'personalityMappingKeep' : 'personalityMappingText')}</Text.p>
      </div>
    </div>
    <ol className="studio-mapping-progress__stages" aria-label={t('personalityStages')}>
      {STAGES.map((stage, index) => {
        const state = index < current ? 'done' : index === current ? 'current' : 'next';
        const Icon = state === 'done' ? IconCheck : IconCircle;
        return <li key={stage} className={`is-${state}`} aria-current={state === 'current' ? 'step' : undefined}>
          <span className="studio-mapping-progress__mark">{state === 'current' ? <span className="studio-mapping-progress__pulse" aria-hidden="true" /> : <Icon aria-hidden="true" />}</span>
          <Text.caption contrast={state === 'next' ? 5 : 8}>{t(`personalityStep_${stage}`)}</Text.caption>
        </li>;
      })}
    </ol>
    <ul className="studio-mapping-progress__sources">
      {mapping.sources.map(source => {
        const state = sourceState(source, mapping);
        const duration = formatDuration(source.duration_seconds);
        // A caption keeps to one line: its own line breaks would otherwise turn on `pre-line`.
        const caption = (source.caption || '').replace(/\s+/g, ' ').trim() || t('personalityUntitled');
        return <li key={source.video_id} className={`is-${state}`}>
          <span className="studio-mapping-progress__thumb">
            {source.preview?.url ? <img src={source.preview.url} alt="" /> : <IconMovie aria-hidden="true" />}
            {duration && <span className="studio-mapping-progress__duration"><Text.caption weight={500}>{duration}</Text.caption></span>}
          </span>
          <Text.span className="studio-mapping-progress__caption" contrast={8} title={caption}>{caption}</Text.span>
          <SourceChip t={t} source={source} state={state} />
        </li>;
      })}
    </ul>
  </div>;
}
